import React, { useEffect, useRef, useCallback } from 'react';
import type { CategorieMarqueur } from '../../types/map';
import { CloseIcon } from '../icons/CloseIcon';
import './CategoriesPickerSheet.css';
import { resolveAssetUrl } from '../../config/api';

export interface CategoriesPickerSheetProps {
  categories: CategorieMarqueur[];
  checkedCategorieIds: Set<number>;
  onToggleCategorie: (id: number) => void;
  onClose: () => void;
}

/**
 * Tiroir plein écran (mobile) listant toutes les catégories « living lab ».
 *
 * Contexte : dans un iframe bas (ex. 550px de haut), la liste de catégories
 * intégrée au panneau de recherche ne dispose plus que de quelques dizaines
 * de pixels de hauteur (cf. `.search-panel__zone--scroll`), ce qui force un
 * ascenseur imbriqué peu utilisable dès que le nombre de catégories dépasse
 * une poignée. Ce composant déporte la sélection dans un tiroir dédié qui
 * profite de toute la hauteur de l'écran, quel que soit le nombre de
 * catégories (7 aujourd'hui, potentiellement plus demain).
 *
 * Ouvert depuis le bouton résumé de `SearchPanel` (mobile uniquement) ;
 * ferme via Echap, clic sur le fond, ou bouton « Valider ».
 */
export const CategoriesPickerSheet: React.FC<CategoriesPickerSheetProps> = ({
  categories,
  checkedCategorieIds,
  onToggleCategorie,
  onClose,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    sheetRef.current?.focus();
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="categories-picker-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Choisissez votre living lab"
      onClick={handleBackdropClick}
    >
      <div className="categories-picker" ref={sheetRef} tabIndex={-1}>
        <div className="categories-picker__header">
          <h2 className="categories-picker__title">Living lab</h2>
          <button
            type="button"
            className="categories-picker__close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <div className="categories-picker__list">
          {categories.map((cat) => (
            <label key={cat.id} className="categories-picker__option">
              <input
                type="checkbox"
                checked={cat.id != null && checkedCategorieIds.has(cat.id)}
                onChange={() => cat.id != null && onToggleCategorie(cat.id)}
              />
              <span className="categories-picker__option-icon" aria-hidden="true">
                {cat.typeIcone === 'image' && cat.imageMarqueur ? (
                  <img
                    src={resolveAssetUrl(cat.imageMarqueur)}
                    alt=""
                    className="categories-picker__option-icon--img"
                  />
                ) : (
                  <span
                    className="categories-picker__option-icon--dot"
                    style={{ background: cat.couleur || '#FF5733' }}
                  />
                )}
              </span>
              <span>{cat.nom}</span>
            </label>
          ))}
        </div>

        <button type="button" className="categories-picker__submit" onClick={onClose}>
          Valider
        </button>
      </div>
    </div>
  );
};

export default CategoriesPickerSheet;
